import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import {
  required,
  composeValidators,
  bookingDatesRequired,
  moneySubUnitAmountAtLeast,
  bookingDatesMinimum,
  bookingConflictWithBookedDays,
} from '../../util/validators';
import { Form, PrimaryButton, FieldDateRangeInput, FieldCurrencyInput } from '../../components';
import { dateFromAPIToLocalNoon } from '../../util/dates';
import css from './RescheduleSaleForm.css';
import config from '../../config';
import EstimatedBreakdownMaybe from './EstimatedBreakdownMaybe';
import debounce from 'lodash/debounce';
import { isInclusivelyAfterDay, isInclusivelyBeforeDay, isSameDay } from 'react-dates';
import moment from 'moment';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { isDayNotAbleToBook } from '../../components/FieldDateRangeInput/DateRangeInput.helpers';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';

const { Money } = sdkTypes;

const DEBOUNCE_WAIT_TIME = 1000;

const identity = v => v;
const createTimeSlots = (transaction, oldTimeSlots) => {
  if ( !transaction || !transaction.booking ) {
    return oldTimeSlots;
  }

  const { booking } = transaction;
  if (!booking) return ;
  const { displayStart, displayEnd, seats } = booking?.attributes;

  const timeSlots = [];

  // Loop through each day between startDate and endDate
  let currentDate = new Date(displayStart);
  while (currentDate <= displayEnd) {
    // Create a time slot object for the current day
    const timeSlot = {
      type: "timeSlot",
      attributes: {
        type: "time-slot/day",
        seats,
        start: currentDate.toISOString(),
        end: new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 1).toISOString()
      }
    };

    timeSlots.push(timeSlot);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return [
    ...timeSlots,
    ...(oldTimeSlots? oldTimeSlots : [] ),
  ];
}

const RescheduleSaleFormComponent = props => {
  const [isFetchedValue, setIsFetchedValue] = useState(null);
  const [isFetched, setIsFetched] = useState(false);
  const [bookedDays, setBookedDays] = useState([]);

  useEffect(() => {
    const { timeSlots, transaction } = props;
    const { start, end } = get(transaction, 'booking.attributes', {});
    const daysBetweenStartDateAndEndDate = moment(end).diff(moment(start), 'days');

    const txCurrentBookingTimeFrame = Array(daysBetweenStartDateAndEndDate)
      .fill(1)
      .map((day, index) => dateFromAPIToLocalNoon(moment(start).add(index, 'days')));
    if (timeSlots && timeSlots.length !== 0) {
      const result = [];

      const today = dateFromAPIToLocalNoon(
        moment()
          .utc()
          .startOf('day')
          .toDate()
      );
      const firstAvailableTimeslot = dateFromAPIToLocalNoon(timeSlots[0].attributes.start);
      const diffFromToday = moment(firstAvailableTimeslot).diff(moment(today), 'days');

      if (diffFromToday > 0) {
        for (let t = 0; t < diffFromToday; t++) {
          result.push(
            moment(today)
              .add(t, 'day')
              .toDate()
          );
        }
      }

      for (let i = 0; i < timeSlots.length - 1; i++) {
        const before = dateFromAPIToLocalNoon(timeSlots[i].attributes.start);
        const after = dateFromAPIToLocalNoon(timeSlots[i + 1].attributes.start);

        const diff = moment(after).diff(moment(before), 'days');

        if (diff === 1) {
          continue;
        }

        for (let t = 1; t < diff; t++) {
          result.push(
            moment(before)
              .add(t, 'day')
              .toDate()
          );
        }
      }
      const newResult = result.filter(
        day =>
          !(
            txCurrentBookingTimeFrame.findIndex(dayInTimeFrame => isEqual(day, dayInTimeFrame)) !==
            -1
          )
      );
      setBookedDays(newResult);
    }
  }, [props.timeSlots]);

  const handleOnChange = formValues => {
    const {
      listingId,
      transactionId,
      onFetchReofferTransactionLineItem,
      fetchLineItemsInProgress,
      seats,
      isOwnListing,
    } = props;
    const { bookingDates, price } = formValues ? formValues.values : {};
    const { startDate, endDate } = bookingDates || {};

    const hasSeatData = parseInt(seats) > 0;
    const isValidPrice = price && price.amount >= 100;

    if (startDate && endDate && hasSeatData && isValidPrice && price && !fetchLineItemsInProgress) {
      onFetchReofferTransactionLineItem({
        bookingData: {
          startDate,
          endDate,
          seats,
          transactionId: transactionId.uuid,
          reofferPrice: price,
        },
        listingId,
        isOwnListing,
      });
      setIsFetchedValue({ bookingDates, price });
      if (!isFetched) setIsFetched(true);
    }
  };

  const debouncedOnChange = debounce(handleOnChange, DEBOUNCE_WAIT_TIME, {
    leading: false,
    trailing: true,
  });

  return (
    <FinalForm
      {...props}
      initialValues={isFetchedValue ? isFetchedValue : props.initialValues}
      render={fieldRenderProps => {
        const {
          className,
          rootClassName,
          disabled,
          handleSubmit,
          intl,
          invalid,
          timeSlots,
          values,
          lineItems,
          fetchLineItemsInProgress,
          fetchLineItemsError,
          rescheduleInProgress,
          rescheduleSaleError,
          unitType,
          formId,
          seats,
          deliveryData,
          listing,
          transaction,
        } = fieldRenderProps;

        const { startDate, endDate } = (values && values.bookingDates) || {};
        const { price } = values || {};

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = fetchLineItemsInProgress || rescheduleInProgress;
        const submitDisabled = invalid || disabled || submitInProgress;

        const { minRent, pricingType } = get(listing, 'attributes.publicData', {});

        const bookingData =
          startDate && endDate
            ? {
                unitType: config.bookingUnitType,
                startDate,
                endDate,
                seats,
                deliveryData,
              }
            : null;

        const rescheduleStartDateLabel = intl.formatMessage({
          id: 'RescheduleSaleForm.rescheduleStartDateLabel',
        });
        const rescheduleEndDateLabel = intl.formatMessage({
          id: 'RescheduleSaleForm.rescheduleEndDateLabel',
        });
        const rescheduleBookingStartPlaceHolder = intl.formatMessage({
          id: 'RescheduleSaleForm.rescheduleBookingStartPlaceHolder',
        });
        const rescheduleBookingEndPlaceHolder = intl.formatMessage({
          id: 'RescheduleSaleForm.rescheduleBookingEndPlaceHolder',
        });
        const requiredMessage = intl.formatMessage({
          id: 'BookingDatesForm.requiredDate',
        });
        const startDateErrorMessage = intl.formatMessage({
          id: 'RescheduleSaleForm.bookingStartDateRequiredMessage',
        });
        const endDateErrorMessage = intl.formatMessage({
          id: 'RescheduleSaleForm.bookingEndDateRequiredMessage',
        });
        const bookingDatePeriodErrorMessage = intl.formatMessage(
          {
            id: 'FieldDateRangeInput.bookingDatePeriodError',
          },
          {
            minRent: `${minRent} ${pricingType}${minRent > 1 ? 's' : ''}`,
          }
        );
        const bookingConflictErrorMessage = intl.formatMessage({
          id: 'FieldDateRangeInput.bookingConflictErrorMessage',
        });

        const priceRequired = required(
          intl.formatMessage({
            id: 'RescheduleSaleForm.priceRequired',
          })
        );
        const minPrice = new Money(100, config.currency);
        const minPriceRequired = moneySubUnitAmountAtLeast(
          intl.formatMessage(
            {
              id: 'RescheduleSaleForm.priceTooLow',
            },
            {
              minPrice: formatMoney(intl, minPrice),
            }
          ),
          100
        );
        const priceValidators = composeValidators(priceRequired, minPriceRequired);

        const priceLabel = intl.formatMessage({ id: 'RescheduleSaleForm.priceLabel' });
        const pricePlaceHolder = intl.formatMessage({ id: 'RescheduleSaleForm.pricePlaceHolder' });

        const showEstimatedBreakdown =
          bookingData &&
          lineItems &&
          price &&
          !fetchLineItemsInProgress &&
          !fetchLineItemsError &&
          isFetched;

        const bookingInfoMaybe = showEstimatedBreakdown ? (
          <div className={css.priceBreakdownContainer}>
            <EstimatedBreakdownMaybe
              userRole="provider"
              bookingData={bookingData}
              lineItems={lineItems}
            />
          </div>
        ) : null;

        const bookingInfoErrorMaybe =
          fetchLineItemsError || rescheduleSaleError ? (
            <span className={css.sideBarError}>
              <FormattedMessage id="RescheduleSaleForm.fetchLineItemsError" />
            </span>
          ) : null;

        const isDayHighlighted = day => isDayNotAbleToBook(timeSlots, minRent, pricingType)(day);

        const endOfRange = config.dayCountAvailableForBooking - 1;
        const isOutsideRange = day =>
          startDate
            ? !isInclusivelyBeforeDay(
            day,
            moment(startDate)
              .clone()
              .add(endOfRange, 'days')
          ) || !isInclusivelyAfterDay(day, moment())
            : null;
        const isDayBlocked = startDate
          ? day => {
            return bookedDays.find(days => {
              const localStartDate = dateFromAPIToLocalNoon(days);
              return isSameDay(day, moment(localStartDate));
            });
          }
          : null;

        const timeSlotsWithTransactionBooking = createTimeSlots(transaction, timeSlots);

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <FormSpy
              subscription={{ values: true }}
              onChange={values => debouncedOnChange(values)}
            />

            <FieldDateRangeInput
              className={css.bookingDates}
              name="bookingDates"
              unitType={unitType}
              startDateId={`${formId}.bookingStartDate`}
              startDateLabel={rescheduleStartDateLabel}
              startDatePlaceholderText={rescheduleBookingStartPlaceHolder}
              endDateId={`${formId}.bookingEndDate`}
              endDateLabel={rescheduleEndDateLabel}
              endDatePlaceholderText={rescheduleBookingEndPlaceHolder}
              format={identity}
              timeSlots={timeSlotsWithTransactionBooking}
              useMobileMargins
              validate={composeValidators(
                required(requiredMessage),
                bookingDatesRequired(startDateErrorMessage, endDateErrorMessage),
                bookingDatesMinimum(bookingDatePeriodErrorMessage, minRent, pricingType)
              )}
              isDayHighlighted={isDayHighlighted}
              isOutsideRange={isOutsideRange}
              isDayBlocked={isDayBlocked}
            />

            <FieldCurrencyInput
              id="price"
              name="price"
              className={css.priceInput}
              autoFocus
              label={priceLabel}
              placeholder={pricePlaceHolder}
              currencyConfig={config.currencyConfig}
              validate={priceValidators}
            />

            {bookingInfoMaybe}
            {bookingInfoErrorMaybe}

            <div className={css.submitButtonWrapper}>
              <div className={css.deliveryDescription}>
                <FormattedMessage id="RescheduleSaleForm.deliveryDescription" />
              </div>
              <PrimaryButton
                className={css.submitButton}
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
              >
                <FormattedMessage id="RescheduleSaleForm.submit" />
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

RescheduleSaleFormComponent.defaultProps = {
  className: null,
  rootClassName: null,
  unitType: config.bookingUnitType,
  onChangeAvailabilityExceptionCurrentBooking: () => {},
};

const { func, string } = PropTypes;

RescheduleSaleFormComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  unitType: propTypes.bookingUnitType,
  onChangeAvailabilityExceptionCurrentBooking: func.isRequired,
  exceptionId: string,
};

const RescheduleSaleForm = compose(injectIntl)(RescheduleSaleFormComponent);
RescheduleSaleForm.displayName = 'RescheduleSaleForm';

export default RescheduleSaleForm;
